"use client";
import React, { useEffect, useImperativeHandle } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import type { FormProps } from './types'
import { FormContextProvider } from './FormContext'
import { yupResolver, } from '@hookform/resolvers/yup'
import { Form as AForm, type FormProps as AFormProps } from 'antd';
import type { ObjectSchema } from 'yup'

// 支持类型推导的Form组件
function Form<T extends FieldValues>({
  form,
  children,
  schema,
  onChange,
  defaultValues,
  onFinish,
  ref,
  isView,
  isQuery,
  component = false,
  ...restProps
}: FormProps<T> & AFormProps<T>,) {

  const _form = useForm<T>({
    resolver: schema ? (yupResolver(schema) as any) : undefined,
    values: defaultValues as any,
  })
  const newForm = form ?? _form

  useEffect(() => {
    let unsub: (() => void) | undefined
    if (onChange) {
      const subscription = newForm.watch((data) => {
        onChange(data as T)
      })
      // react-hook-form v7+ watch returns a subscription object with unsubscribe method
      unsub = typeof subscription === 'function' ? subscription : subscription?.unsubscribe
    }
    return () => {
      if (typeof unsub === 'function') {
        unsub()
      }
    }
  }, [onChange, newForm])

  useImperativeHandle(ref, () => ({
    form: newForm,
  }))

  return (
    <FormProvider {...newForm}>
      <FormContextProvider value={{ schema: schema as ObjectSchema<FieldValues> | undefined, isView, isQuery }}>
        <AForm
          component={component}
          labelAlign='left'
          {...restProps}
          onFinish={(values: T) => {
            if (onFinish) {
              newForm.handleSubmit(onFinish)(values as any);
            }
          }}
        >
          {children}
        </AForm>
      </FormContextProvider>
    </FormProvider>
  )
}

export default Form
